import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, detailText } from "@/lib/api";
import { formatPhoneLocal } from "@/lib/utils";
import {
  UserPlus,
  Copy,
  Check,
  MessageCircle,
  Loader2,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";

export function formatWaNumber(phone) {
  let cleaned = String(phone || "").replace(/\D/g, "");
  if (cleaned.startsWith("0")) {
    cleaned = "62" + cleaned.slice(1);
  }
  return cleaned;
}

export function generateWaInviteMessage({ partnerName, phone, tempPassword }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://weddly.id";
  const loginUrl = `${origin}/login`;
  const greeting = partnerName ? `Halo ${partnerName},` : "Sayang,";

  return `${greeting} aku baru buatkan akun buat kamu di Weddly Wedding Planner biar kita bisa atur budget, checklist, & Couple Talk bareng-bareng!

Yuk login di sini:
Link: ${loginUrl}
No HP: ${phone}
Password Sementara: ${tempPassword}

(Password bisa langsung kamu ganti setelah login)`;
}

export default function PartnerInviteModal({
  open,
  onOpenChange,
  defaultName = "",
  onSuccess,
}) {
  const [name, setName] = useState(defaultName);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (defaultName && !name) {
      setName(defaultName);
    }
  }, [defaultName, name]);

  const handleClose = (nextOpen) => {
    if (!nextOpen) {
      if (inviteResult && onSuccess) {
        onSuccess(inviteResult);
      }
      setInviteResult(null);
      setPhone("");
    }
    onOpenChange?.(nextOpen);
  };

  const handleInvite = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) {
      return toast.error("Nomor HP pasangan wajib diisi");
    }

    setLoading(true);
    try {
      const res = await api.post("/partners/invite", {
        phone: phone.trim(),
        name: name.trim(),
      });

      const result = {
        password: res.data?.temp_password,
        wa_sent: res.data?.wa_sent,
        phone: phone.trim(),
        name: name.trim(),
        partner_user_id: res.data?.partner_user_id,
      };

      setInviteResult(result);
      if (onSuccess) {
        onSuccess(result);
      }

      if (res.data?.wa_sent) {
        toast.success("Akun pasangan berhasil dibuat & notifikasi WA terkirim!");
      } else {
        toast.success("Akun pasangan berhasil dibuat!");
      }
    } catch (err) {
      toast.error(
        detailText(err?.response?.data?.detail) || "Gagal membuat akun pasangan"
      );
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async () => {
    if (!inviteResult?.password) return;
    try {
      await navigator.clipboard.writeText(inviteResult.password);
      setCopied(true);
      toast.success("Password sementara disalin");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Gagal menyalin password");
    }
  };

  const openWhatsApp = () => {
    if (!inviteResult) return;
    const waPhone = formatWaNumber(inviteResult.phone);
    const msg = generateWaInviteMessage({
      partnerName: inviteResult.name,
      phone: inviteResult.phone,
      tempPassword: inviteResult.password,
    });
    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md sm:rounded-2xl" data-testid="partner-invite-modal">
        <DialogHeader>
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary mb-2">
            <UserPlus className="h-5 w-5" />
          </div>
          <DialogTitle className="font-serif text-xl sm:text-2xl">
            {inviteResult ? "Akun Pasangan Berhasil Dibuat!" : "Undang Pasangan"}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            {inviteResult
              ? "Bagikan kredensial login berikut ke pasanganmu agar bisa langsung mengakses workspace."
              : "Masukkan nomor HP pasanganmu. Akun langsung aktif dan pasangan bisa langsung login tanpa perlu persetujuan admin."}
          </DialogDescription>
        </DialogHeader>

        {!inviteResult ? (
          <form onSubmit={handleInvite} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="partner-modal-name">Nama Pasangan (Opsional)</Label>
              <Input
                id="partner-modal-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Rina / Dimas"
                disabled={loading}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="partner-modal-phone">Nomor WhatsApp Pasangan</Label>
              <Input
                id="partner-modal-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="08123456789"
                inputMode="tel"
                disabled={loading}
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground">
                Gunakan nomor WhatsApp aktif untuk kemudahan kirim pesan login.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full h-11 text-sm font-medium mt-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Membuat Akun Pasangan...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Buat & Siapkan Undangan
                </>
              )}
            </Button>
          </form>
        ) : (
          <div className="space-y-4 pt-2">
            {inviteResult.wa_sent ? (
              <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-50 border border-green-200 text-xs text-green-900 leading-relaxed">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold block">Notifikasi Otomatis Terkirim</span>
                  Kredensial telah dikirimkan ke WhatsApp{" "}
                  <strong>{formatPhoneLocal(inviteResult.phone)}</strong>.
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
                <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold block">Kirim Kredensial ke Pasangan</span>
                  Gunakan tombol WhatsApp di bawah atau salin password sementara berikut untuk diberikan ke pasangan Anda.
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Nomor HP Login</span>
                <span className="font-mono font-medium text-foreground">
                  {formatPhoneLocal(inviteResult.phone)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/60">
                <span className="text-xs text-muted-foreground">Password Sementara:</span>
                <div className="flex items-center gap-1.5">
                  <code className="font-mono text-sm font-semibold bg-background px-2 py-0.5 rounded border border-border">
                    {inviteResult.password}
                  </code>
                  <Button
                    type="button"
                    size="icon"
                    variant={copied ? "default" : "outline"}
                    className="h-7 w-7"
                    onClick={copyPassword}
                    title="Salin Password"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            </div>

            <Button
              type="button"
              onClick={openWhatsApp}
              className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm flex items-center justify-center gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              Kirim Undangan via WhatsApp
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              className="w-full rounded-full text-xs h-9"
            >
              Selesai & Tutup
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
